import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { zodResolver } from '@hookform/resolvers/zod';
import { MainContents } from '@/components/MainContents';
import Loading from '@/components/Loading';
import type { SubmitHandler } from 'react-hook-form';
import { useForm, FormProvider } from 'react-hook-form';
import useCustomFields from '../../custom_categories/hooks/useCustomFields';
import Results from '../components/Results';
import SearchForm from '../components/SearchForm';
import type { FormValue } from '../searchSchema';
import { searchSchema } from '../searchSchema';
import useFetchCandidateProfiles, { type CustomFieldQuery } from '../api/useFetchCandidateProfiles';
import useFetchCandidateBoards from '../api/useFetchCandidateBoards';
import clsx from 'clsx';
import { useAtom } from 'jotai';
import { candidateScreenType } from '../atom/candidatesAtom';
import CandidatesBoardWrapper from '../components/CandidatesBoardWrapper';
import useFetchCandidateProfilesCustomFields from '../api/useFetchCandidateProfilesCustomFields';
import CandidateLoadingCard from '../components/CandidateLoadingCard';
import BackToSearchBar from '@/components/BackToSearchBar';
import CandidatesSelectionBar, { type BulkActionType } from '../components/CandidatesSelectionBar';
import useFetchIam from '../../api/useFetchIam';
import BulkChangeOrganizationUserModal from '../components/BulkChangeOrganizationUserModal';
import BulkTagModal from '../components/BulkTagModal';
import BulkDeleteConfirmModal from '../components/BulkDeleteConfirmModal';
import BulkUpdateCustomFieldsModal from '../components/BulkUpdateCustomFieldsModal';
import BulkSuggestJobPostsModal from '../components/BulkSuggestJobPostsModal';
import { useQueryClient } from '@tanstack/react-query';
import qs from 'qs';
import compact from '@/utils/compact';
import useActiveUsers from '../../active_users/hooks/useActiveUsers';
import useUpdateCandidateProfile from '../api/useUpdateCandidateProfile';
import { showErrorToast } from '../../job_change_processes/lib/toast';
import useFetchCandidateMailSetting from '../../candidate_mails/api/useFetchCandidateMailSetting';
import CandidateMailComposeModal from '../../candidate_mails/components/CandidateMailComposeModal';
import {
  hasEmail,
  toRecipient,
  type CandidateRecipient,
} from '../../candidate_mails/lib/recipients';

const SEARCH_QUERY_STORAGE_KEY = 'candidateProfilesSearchQuery';

// 廃止したソート項目が localStorage / URL に残っていても既定順へ戻す
const REMOVED_SORT_VALUES = ['first_interviewed_at'];
const normalizeSort = (value?: string | null) =>
  value && !REMOVED_SORT_VALUES.includes(value) ? value : 'updated_at';

type MailDraft = { recipients: CandidateRecipient[]; excludedCount: number };

type SearchQuery = {
  keyword: string;
  organization_user_ids?: number[];
  custom_tag_ids?: number[];
  custom_tag_match?: 'any';
  custom_fields?: CustomFieldQuery[];
  age?: {
    min?: number;
    max?: number;
  };
  include_finished?: boolean;
};

type StoredSearch = { searchQuery: SearchQuery; sort: string };

const parseSearchQueryFromUrl = (search: string): StoredSearch | null => {
  const q = qs.parse(search, { ignoreQueryPrefix: true });
  if (Object.keys(q).length === 0) return null;

  let parsedCustomFields: CustomFieldQuery[] | undefined;
  if (q.custom_fields) {
    try {
      parsedCustomFields = JSON.parse(q.custom_fields as string) as CustomFieldQuery[];
    } catch (error) {
      console.error('Failed to parse custom_fields from URL:', error);
      parsedCustomFields = undefined;
    }
  }

  return {
    searchQuery: {
      keyword: (q.keyword as string) ?? '',
      organization_user_ids: q.organization_user_ids
        ? (Array.isArray(q.organization_user_ids)
            ? q.organization_user_ids
            : [q.organization_user_ids]
          ).map(Number)
        : q.organization_user_id
          ? [Number(q.organization_user_id)]
          : undefined,
      custom_tag_ids: q.custom_tag_ids
        ? Array.isArray(q.custom_tag_ids)
          ? q.custom_tag_ids.map(Number)
          : [+q.custom_tag_ids]
        : undefined,
      custom_tag_match: q.custom_tag_match === 'any' ? 'any' : undefined,
      custom_fields: parsedCustomFields,
      age:
        q['age[min]'] || q['age[max]']
          ? {
              min: q['age[min]'] ? Number(q['age[min]']) : undefined,
              max: q['age[max]'] ? Number(q['age[max]']) : undefined,
            }
          : undefined,
      include_finished: q.include_finished === 'true',
    },
    sort: normalizeSort(q.sort as string),
  };
};

const loadSearchQueryFromLocalStorage = (): StoredSearch | null => {
  try {
    const stored = localStorage.getItem(SEARCH_QUERY_STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    return {
      searchQuery: {
        keyword: parsed.keyword ?? '',
        organization_user_ids: Array.isArray(parsed.organization_user_ids)
          ? parsed.organization_user_ids.map(Number)
          : parsed.organization_user_id != null
            ? [Number(parsed.organization_user_id)]
            : undefined,
        custom_tag_ids: parsed.custom_tag_ids,
        custom_tag_match: parsed.custom_tag_match === 'any' ? 'any' : undefined,
        custom_fields: parsed.custom_fields,
        age: parsed.age,
        include_finished: parsed.include_finished ?? false,
      },
      sort: normalizeSort(parsed.sort),
    };
  } catch (error) {
    console.error('Failed to load search query from localStorage:', error);
    return null;
  }
};

const saveSearchQueryToLocalStorage = (sq: SearchQuery, sortValue: string) => {
  try {
    const dataToSave = {
      ...sq,
      sort: sortValue,
    };
    const serialized = JSON.stringify(dataToSave);
    localStorage.setItem(SEARCH_QUERY_STORAGE_KEY, serialized);
  } catch (error) {
    console.error('Failed to save search query to localStorage:', error);
  }
};

const buildSearchQueryString = (sq: SearchQuery, sortValue: string) => {
  const queryParams: Record<string, string | number | number[] | undefined> = {
    sort: sortValue,
  };

  if (sq.keyword) {
    queryParams.keyword = sq.keyword;
  }

  if (sq.organization_user_ids && sq.organization_user_ids.length > 0) {
    queryParams.organization_user_ids = sq.organization_user_ids;
  }

  if (sq.custom_tag_ids && sq.custom_tag_ids.length > 0) {
    queryParams.custom_tag_ids = sq.custom_tag_ids;
  }

  if (sq.custom_tag_match) {
    queryParams.custom_tag_match = sq.custom_tag_match;
  }

  if (sq.custom_fields && sq.custom_fields.length > 0) {
    queryParams.custom_fields = JSON.stringify(sq.custom_fields);
  }

  if (sq.age) {
    if (sq.age.min !== undefined) {
      queryParams['age[min]'] = sq.age.min;
    }
    if (sq.age.max !== undefined) {
      queryParams['age[max]'] = sq.age.max;
    }
  }

  if (sq.include_finished) {
    queryParams.include_finished = 'true';
  }

  return qs.stringify(compact(queryParams), { arrayFormat: 'brackets' });
};

// 条件なしで一度取得してから復元すると、戻った直後に絞り込みの外れた一覧が出る
const resolveInitialSearch = (): StoredSearch =>
  parseSearchQueryFromUrl(location.search) ??
  loadSearchQueryFromLocalStorage() ?? { searchQuery: { keyword: '' }, sort: 'updated_at' };

export default function CandidatesTemplate() {
  useDocumentTitle('求職者管理');
  const queryClient = useQueryClient();
  const { data: iam } = useFetchIam();
  const canBulkDelete = Boolean(iam?.current_user?.is_admin || iam?.current_user?.is_support);
  const currentUserId = iam?.current_user?.id;
  const canSendMail = (iam?.sidebar_items ?? []).some(
    (item) => item.key === 'candidate_mails' && item.enabled
  );
  const { data: mailSettingData } = useFetchCandidateMailSetting({ enabled: canSendMail });
  const { data: activeUsersData } = useActiveUsers();
  const activeUsers = activeUsersData?.activeUsers ?? [];
  const { mutateAsync: updateCandidate } = useUpdateCandidateProfile();
  const [screenType, setScreenType] = useAtom(candidateScreenType);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkActionModal, setBulkActionModal] = useState<BulkActionType | null>(null);
  const [isSuggestModalOpen, setIsSuggestModalOpen] = useState(false);
  const [mailDraft, setMailDraft] = useState<MailDraft | null>(null);

  const handleCheckedChange = useCallback((id: number, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  const handleDeselectAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const handleSuggestJobPosts = useCallback(() => {
    setIsSuggestModalOpen(true);
  }, []);

  const handleSuggestSuccess = useCallback(() => {
    setIsSuggestModalOpen(false);
    setSelectedIds(new Set());
    queryClient.invalidateQueries({ queryKey: ['candidateProfiles'] });
  }, [queryClient]);

  const handleBulkAction = useCallback((action: BulkActionType) => {
    setBulkActionModal(action);
  }, []);

  const handleBulkActionSuccess = useCallback(() => {
    setBulkActionModal(null);
    setSelectedIds(new Set());
    queryClient.invalidateQueries({ queryKey: ['candidateProfiles'] });
  }, [queryClient]);

  const handleUpdateAssignees = useCallback(
    async (candidateProfileId: number, userIds: number[]) => {
      try {
        await updateCandidate({ candidateProfileId, organization_user_ids: userIds });
        await queryClient.invalidateQueries({ queryKey: ['candidateProfiles'] });
      } catch {
        showErrorToast('担当者の更新に失敗しました');
      }
    },
    [updateCandidate, queryClient]
  );

  const { data: candidateBoardsData } = useFetchCandidateBoards();

  const handleBoardChanged = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['candidateProfiles'] });
  }, [queryClient]);
  const searchRef = useRef<HTMLDivElement>(null);
  const [initialSearch] = useState(resolveInitialSearch);
  const [searchQuery, setSearchQuery] = useState<SearchQuery>(initialSearch.searchQuery);
  const [sort, setSort] = useState(initialSearch.sort);
  // デバウンス後の呼び出しでも直前の条件にマージするため最新値を ref に持つ
  const searchQueryRef = useRef(searchQuery);
  useEffect(() => {
    searchQueryRef.current = searchQuery;
  }, [searchQuery]);
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useFetchCandidateProfiles({ ...searchQuery, sort }, { enabled: screenType === 'list' });
  const { data: customFields } = useFetchCandidateProfilesCustomFields();
  const { data: customFieldData } = useCustomFields();

  const form = useForm<FormValue>({
    resolver: zodResolver(searchSchema),
    defaultValues: {
      customTagIds: [],
      customTagMatch: 'all',
      organizationUserIds: [],
      includeFinished: false,
    },
  });

  const onSubmit: SubmitHandler<FormValue> = (data) => {
    try {
      const sq: typeof searchQuery = {
        keyword: data.keyword ?? '',
        organization_user_ids: data.organizationUserIds?.length
          ? data.organizationUserIds
          : undefined,
        custom_tag_ids: data.customTagIds,
        custom_tag_match: data.customTagMatch === 'any' ? 'any' : undefined,
        include_finished: data.includeFinished ?? false,
      };

      if (data.customFields && Array.isArray(data.customFields)) {
        sq.custom_fields = data.customFields;
      }

      if (data.age) {
        sq.age = {
          min: data.age.min ?? undefined,
          max: data.age.max ?? undefined,
        };
      }

      setSearchQuery(sq);

      history.pushState(null, '', `?${buildSearchQueryString(sq, sort)}`);
      saveSearchQueryToLocalStorage(sq, sort);
    } catch (error) {
      console.error('Error in onSubmit:', error);
    }
  };

  const setSearchQueryFromQueryString = () => {
    const fromUrl = parseSearchQueryFromUrl(location.search);
    let restoredSearchQuery: typeof searchQuery;
    let restoredSort: string;

    if (fromUrl) {
      restoredSearchQuery = fromUrl.searchQuery;
      restoredSort = fromUrl.sort;

      saveSearchQueryToLocalStorage(restoredSearchQuery, restoredSort);
    } else {
      const stored = loadSearchQueryFromLocalStorage();
      if (stored) {
        restoredSearchQuery = stored.searchQuery;
        restoredSort = stored.sort;
        history.replaceState(
          null,
          '',
          `?${buildSearchQueryString(restoredSearchQuery, restoredSort)}`
        );
      } else {
        restoredSearchQuery = {
          keyword: '',
        };
        restoredSort = 'updated_at';
      }
    }

    setSearchQuery(restoredSearchQuery);
    setSort(restoredSort);
    form.setValue('keyword', restoredSearchQuery.keyword ?? '');
    form.setValue('organizationUserIds', restoredSearchQuery.organization_user_ids ?? []);
    form.setValue('customTagIds', restoredSearchQuery.custom_tag_ids ?? []);
    form.setValue('customTagMatch', restoredSearchQuery.custom_tag_match ?? 'all');
    form.setValue('includeFinished', restoredSearchQuery.include_finished ?? false);
    if (restoredSearchQuery.custom_fields) {
      form.setValue('customFields', restoredSearchQuery.custom_fields);
    }
    if (restoredSearchQuery.age) {
      form.setValue('age', {
        min: restoredSearchQuery.age.min ?? null,
        max: restoredSearchQuery.age.max ?? null,
      });
    }
  };

  const handleBoardSearchChange = (change: {
    keyword?: string;
    organizationUserIds?: number[];
  }) => {
    const next: SearchQuery = { ...searchQueryRef.current };
    if (change.keyword !== undefined) {
      next.keyword = change.keyword;
    }
    if (change.organizationUserIds !== undefined) {
      next.organization_user_ids = change.organizationUserIds.length
        ? change.organizationUserIds
        : undefined;
    }
    searchQueryRef.current = next;
    setSearchQuery(next);
    history.replaceState(null, '', `?${buildSearchQueryString(next, sort)}`);
    saveSearchQueryToLocalStorage(next, sort);
    form.setValue('keyword', next.keyword);
    form.setValue('organizationUserIds', next.organization_user_ids ?? []);
  };

  useEffect(() => {
    setSearchQueryFromQueryString();
    window.addEventListener('popstate', setSearchQueryFromQueryString);
    return () => window.removeEventListener('popstate', setSearchQueryFromQueryString);
  }, []);

  const [customFieldsLoaded, setCustomFieldsLoaded] = useState(false);

  useEffect(() => {
    if (customFields && customFields.records && customFields.records.length > 0) {
      setCustomFieldsLoaded(true);
    }
  }, [customFields]);

  const candidateProfiles = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.records);
  }, [data?.pages]);

  // 選択は検索条件を変えても残るため、現在の一覧から外れた求職者も宛先にできるよう保持する
  const loadedProfilesRef = useRef(new Map<number, (typeof candidateProfiles)[number]>());
  useEffect(() => {
    candidateProfiles.forEach((cp) => loadedProfilesRef.current.set(cp.id, cp));
  }, [candidateProfiles]);

  const handleSendMail = useCallback(() => {
    const selectedProfiles = Array.from(selectedIds).flatMap((id) => {
      const profile = loadedProfilesRef.current.get(id);
      return profile ? [profile] : [];
    });
    const sendableProfiles = selectedProfiles.filter(hasEmail);
    if (sendableProfiles.length === 0) {
      showErrorToast('メールアドレスが登録された求職者が選択されていません');
      return;
    }
    const recipientLimit = mailSettingData?.mail_recipient_limit ?? null;
    if (recipientLimit !== null && sendableProfiles.length > recipientLimit) {
      showErrorToast(`送信先は最大${recipientLimit}件まで選択できます。`);
      return;
    }
    setMailDraft({
      recipients: sendableProfiles.map(toRecipient),
      excludedCount: selectedProfiles.length - sendableProfiles.length,
    });
  }, [mailSettingData, selectedIds]);

  const handleMailFinished = useCallback(() => {
    setMailDraft(null);
    setSelectedIds(new Set());
  }, []);

  const handleScroll = useCallback(() => {
    if (isFetchingNextPage || !hasNextPage) return;

    const { scrollTop, scrollHeight, clientHeight } = document.documentElement;

    if (scrollTop + clientHeight >= scrollHeight - 500) {
      fetchNextPage();
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const debouncedScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleScroll, 100);
    };

    window.addEventListener('scroll', debouncedScroll);
    return () => {
      window.removeEventListener('scroll', debouncedScroll);
      clearTimeout(timeoutId);
    };
  }, [handleScroll]);

  return screenType === 'list' ? (
    <FormProvider {...form}>
      <MainContents>
        <div ref={searchRef}>
          {customFieldsLoaded && (
            <SearchForm
              key={`search-form-${customFieldsLoaded}`}
              totalCount={data?.pages?.[0]?.total_count ?? 0}
              onSubmit={onSubmit}
              sort={sort}
              onChangeSort={setSort}
              setScreenType={setScreenType}
              screenType={screenType}
              customFields={customFields?.records ?? []}
              isLoading={isLoading}
              initialCustomFields={searchQuery.custom_fields}
            />
          )}
        </div>
        <BackToSearchBar targetRef={searchRef} />
        <div className={clsx(screenType === 'list' && 'space-y-6 pb-8')}>
          {isLoading ? (
            <div className="flex flex-col gap-md">
              {Array.from({ length: 10 }).map((_, key) => {
                return <CandidateLoadingCard key={key} />;
              })}
            </div>
          ) : (
            <>
              {candidateProfiles.length > 0 && customFieldData && (
                <Results
                  customFields={customFieldData.customFields}
                  candidateProfiles={candidateProfiles}
                  activeUsers={activeUsers}
                  currentUserId={currentUserId}
                  onUpdateAssignees={handleUpdateAssignees}
                  candidatesBoards={candidateBoardsData?.records ?? []}
                  onBoardChanged={handleBoardChanged}
                  selectedIds={selectedIds}
                  onCheckedChange={handleCheckedChange}
                />
              )}
              {(isLoading || isFetchingNextPage) && candidateProfiles.length > 0 && (
                <div className="flex justify-center py-md">
                  <Loading size="small" />
                </div>
              )}
            </>
          )}
        </div>
      </MainContents>
      <CandidatesSelectionBar
        selectedCount={selectedIds.size}
        onDeselectAll={handleDeselectAll}
        onSuggestJobPosts={handleSuggestJobPosts}
        onBulkAction={handleBulkAction}
        canBulkDelete={canBulkDelete}
        onSendMail={canSendMail ? handleSendMail : undefined}
      />
      <BulkChangeOrganizationUserModal
        ids={Array.from(selectedIds)}
        open={bulkActionModal === 'changeOrganizationUser'}
        onOpenChange={(open) => !open && setBulkActionModal(null)}
        onSuccess={handleBulkActionSuccess}
      />
      <BulkTagModal
        ids={Array.from(selectedIds)}
        mode="add"
        open={bulkActionModal === 'addTag'}
        onOpenChange={(open) => !open && setBulkActionModal(null)}
        onSuccess={handleBulkActionSuccess}
      />
      <BulkTagModal
        ids={Array.from(selectedIds)}
        mode="remove"
        open={bulkActionModal === 'removeTag'}
        onOpenChange={(open) => !open && setBulkActionModal(null)}
        onSuccess={handleBulkActionSuccess}
      />
      <BulkUpdateCustomFieldsModal
        ids={Array.from(selectedIds)}
        open={bulkActionModal === 'updateData'}
        onOpenChange={(open) => !open && setBulkActionModal(null)}
        onSuccess={handleBulkActionSuccess}
      />
      <BulkDeleteConfirmModal
        ids={Array.from(selectedIds)}
        candidateNames={candidateProfiles
          .filter((cp) => selectedIds.has(cp.id))
          .map((cp) => cp.name)}
        open={bulkActionModal === 'deleteData'}
        onOpenChange={(open) => !open && setBulkActionModal(null)}
        onSuccess={handleBulkActionSuccess}
      />
      <BulkSuggestJobPostsModal
        candidateIds={Array.from(selectedIds)}
        open={isSuggestModalOpen}
        onOpenChange={setIsSuggestModalOpen}
        onSuccess={handleSuggestSuccess}
      />
      {mailDraft && (
        <CandidateMailComposeModal
          initialRecipients={mailDraft.recipients}
          excludedCount={mailDraft.excludedCount}
          onClose={() => setMailDraft(null)}
          onFinished={handleMailFinished}
        />
      )}
    </FormProvider>
  ) : (
    <CandidatesBoardWrapper
      search={{ screenType, setScreenType }}
      keyword={searchQuery.keyword}
      organizationUserIds={searchQuery.organization_user_ids}
      onSearchChange={handleBoardSearchChange}
    />
  );
}
